// New medicine watch (Console V8, 2026-10-27). Back end: src/aithora/v3/worklist/med-watch-routes.ts
// (the routes) and ./med-watch.ts (the rules). A nurse, the Facility Admin and the Aithora team get
// the clinical steps; everyone else on the floor gets the medicine and the "Tell the nurse if" signs.
// Text comes back in the viewer's language (users.lang, or ?lang=es).
//
//   GET  /residents/:id/med-watches            the chart card (?all=1 adds closed and stopped)
//   GET  /facilities/:id/med-watches           every open watch, most urgent first
//   POST /med-watches/:id/steps/:stepId/done   Done on a step (nurse or Facility Admin)
//   POST /med-watches/:id/signs {sign, note?}  anyone on the floor reports a sign: a Guardian case opens
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export type MedWatchStepStatus = 'open' | 'snoozed' | 'done' | 'refused' | 'cancelled';

export interface MedWatchStep {
  id: string;
  key: string;
  label: string;
  why: string;
  dayOffset: number;
  dueOn: string;
  dueAt: string;
  status: MedWatchStepStatus;
  late: boolean;
  doneAt: string | null;
  doneById: string | null;
  doneByName: string | null;
  /** The Worklist item behind the step. */
  itemRef: { tab: 'tasks'; sourceId: string; occurrence: string } | null;
}

export interface MedWatchSign { key: string; text: string }

export interface MedWatchReportedSign { key: string; text: string; at: string; byName: string | null; guardianSignalId: string | null }

/** The steps view: a nurse, the Facility Admin and the Aithora team. */
export interface MedWatchView {
  id: string;
  residentId: string;
  residentName: string | null;
  room: string | null;
  medicine: string | null;
  medClass: string;
  className: string;
  startedAt: string;
  startedOn: string;
  startedByName: string | null;
  source: string;
  /** "Day 3 of 30": today's day of the watch, from 1. */
  day: number;
  days: number;
  status: 'open' | 'closed' | 'stopped';
  highAlert: boolean;
  next: MedWatchStep | null;
  steps: MedWatchStep[];
  interactions: string[];
  signs: MedWatchSign[];
  reportedSigns: MedWatchReportedSign[];
  canClose: boolean;
  closedAt: string | null;
  closedByName: string | null;
}

/** The signs view: everyone else on the floor (care staff). Never the clinical steps. */
export interface MedWatchSignsView {
  id: string;
  residentId: string;
  residentName: string | null;
  room: string | null;
  medicine: string | null;
  className: string;
  day: number;
  days: number;
  signs: MedWatchSign[];
}

export type FacilityMedWatches =
  | { real: true; view: 'signs'; title: string; watches: MedWatchSignsView[] }
  | { real: true; view: 'steps'; readOnly: boolean; escalation: string; watches: MedWatchView[] };

export type ResidentMedWatches =
  | { real: true; view: 'signs'; readOnly: boolean; escalation: string; watches: MedWatchSignsView[] }
  | { real: true; view: 'steps'; readOnly: boolean; escalation: string; watches: MedWatchView[] };

export const facilityMedWatches = route<FacilityMedWatches, { lang?: string }>()('GET', `${A}/facilities/:id/med-watches`, { honest: true });

export const residentMedWatches = route<ResidentMedWatches, { all?: string; lang?: string }>()('GET', `${A}/residents/:id/med-watches`, { honest: true });

export const medWatchStepDone = route<{ real: true; actionId: string; watch: MedWatchView | null }, void, { note?: string }>()(
  'POST', `${A}/med-watches/:id/steps/:stepId/done`, { honest: true });

/** 201 with the words to show ("The nurse and the care team have it on Guardian now."). */
export const medWatchReportSign = route<{ real: true; signalId: string; told: string }, void, { sign: string; note?: string }>()(
  'POST', `${A}/med-watches/:id/signs`, { honest: true });

/** Why a watch stops before its steps are done (STOP_REASONS). */
export type MedWatchStopReason = 'medicine_stopped' | 'not_new' | 'other';

/** POST /med-watches/:id/close {note?} ("Result reviewed, close watch"), or {stopReason, note?} to stop it early. */
export const medWatchClose = route<{ real: true; status: 'closed' | 'stopped' }, void, { note?: string; stopReason?: MedWatchStopReason }>()(
  'POST', `${A}/med-watches/:id/close`, { honest: true });

// The names the console pages use for the same shapes and routes.
export type MedWatch = MedWatchView;
export type MedWatchSigns = MedWatchSignsView;
export type MedWatchSignsOnly = MedWatchSignsView;
export type ResidentMedWatchesResponse = ResidentMedWatches;
export const medWatchSign = medWatchReportSign;

export const medWatchApi = { facilityMedWatches, residentMedWatches, medWatchStepDone, medWatchReportSign, medWatchClose } as const;
